import { useEffect, useRef, useState } from "react";
import stringSimilarity from "string-similarity";
import VoiceRecognitionBar from "../design/VoiceRecognitionBar";
import axios from "axios";
import { useRecoilState } from "recoil";
import { readingProblemTypeAtom } from "../atoms/readingProblemTypeAtom";
import { base64ToBlob } from "./ReadingMain";

interface Emoji {
  idx: number;
  emoji: string;
  text: string;
}

interface EmogiData {
  sentence: string;
  emojis: Emoji[];
}

const EmojiQues = ({
  listening,
  transcript,
  resetTranscript,
  toggleListening,
  setCurSentence,
  curSentence,
  sentence,
  setCurContent,
  curContent,
  setShowQues,
  sentenceLenght,
  numberOfContents,
}: {
  listening: boolean;
  transcript: string;
  resetTranscript: () => void;
  toggleListening: () => void;
  setCurSentence: React.Dispatch<React.SetStateAction<number>>;
  curSentence: number;
  sentence: string;
  setCurContent: React.Dispatch<React.SetStateAction<number>>;
  curContent: number;
  setShowQues: React.Dispatch<React.SetStateAction<boolean>>;
  sentenceLenght: number;
  numberOfContents: number;
}) => {
  const [quesNum, setQuesNum] = useState(0);
  const [isVoiceRecognitionRight, setIsVoiceRecognitionRight] = useState(0); // 0 아무텍스트도 없음, 1 잘하셨어요 -1 다시한번 생각해보세요
  const [problemType, setProblemType] = useRecoilState(readingProblemTypeAtom);
  const [emojiQues, setEmojiQues] = useState<EmogiData>({
    sentence: "",
    emojis: [],
  });
  const [nounQuesChangedData, setNounQuesChangedData] = useState<string[]>([]);
  const [numberOfIncorrectEmojiQuestions, setNumberOfIncorrectEmojiQuestions] =
    useState(0);

  const [quesTxt, setQuesTxt] = useState<string>("");
  const [emojiNum, setEmojiNum] = useState(0);

  const targetQues = nounQuesChangedData[quesNum];
  const numberOfQue = nounQuesChangedData.length;
  const audioElement = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    makeNounQues();
  }, [sentence]);

  useEffect(() => {
    if (emojiQues?.emojis && emojiQues.emojis.length > 0) {
      const emojiList = emojiQues.emojis.map((emoji) => emoji.emoji);
      const shuffledOrder = emojiList.sort(() => Math.random() - 0.5); // shuffle
      const shuffledTxt = shuffledOrder.join(" ");

      // 데이터 업데이트
      setNounQuesChangedData(["", emojiQues.sentence, shuffledTxt]);
    }
  }, [emojiQues]);

  const makeNounQues = async () => {
    const res = await axios.get(
      "https://uvlqtxcfrh.us14.qoddiapp.com/reading/emoji",
      { params: { sentence } }
    );
    setEmojiQues(res.data);
    const first = res.data.emojis[0];
    const audioRes = await axios.get(
      "https://uvlqtxcfrh.us14.qoddiapp.com/reading/tts",
      {
        params: { ssml: `<speak>${first.text}</speak>` },
      }
    );
    setQuesTxt(`${first.text} ${first.emoji}`);
    const { audioContent } = audioRes.data;
    const audioBlob = base64ToBlob(audioContent, "audio/mp3");
    const audioUrl = URL.createObjectURL(audioBlob);

    // 오디오 요소 생성 및 바로 실행
    audioElement.current = new Audio(audioUrl);
    audioElement.current.play(); // 바로 오디오 실행
  };

  const onSubmitFirstTranscript = async () => {
    const targetText = emojiQues.emojis[emojiNum].text;
    const similarity = stringSimilarity.compareTwoStrings(
      targetText,
      transcript
    );
    if (listening) {
      toggleListening(); // 음성 인식을 멈춤
      setTimeout(() => resetTranscript(), 200); // 약간의 지연 후 초기화
    } else {
      resetTranscript();
    }
    setIsVoiceRecognitionRight(0);
    if (similarity >= 0.7) {
      const targetEmoji = emojiQues.emojis[emojiNum + 1];
      if (!targetEmoji) {
        setQuesNum((prev) => prev + 1);
        return;
      }
      setQuesTxt(` ${targetEmoji.text} ${targetEmoji.emoji}`);
      setEmojiNum((prev) => prev + 1);
      const audioRes = await axios.get(
        "https://uvlqtxcfrh.us14.qoddiapp.com/reading/tts",
        {
          params: { ssml: `<speak>${targetEmoji.text}</speak>` },
        }
      );
      const { audioContent } = audioRes.data;
      const audioBlob = base64ToBlob(audioContent, "audio/mp3");
      const audioUrl = URL.createObjectURL(audioBlob);

      // 오디오 요소 생성 및 바로 실행
      audioElement.current = new Audio(audioUrl);
      audioElement.current.play(); // 바로 오디오 실행
    } else {
      audioElement.current!.play(); // 바로 오디오 실행
    }
  };

  const onSubmitTranscript = () => {
    const similarity = stringSimilarity.compareTwoStrings(sentence, transcript);
    if (listening) {
      toggleListening(); // 음성 인식을 멈춤
      setTimeout(() => resetTranscript(), 200); // 약간의 지연 후 초기화
    } else {
      resetTranscript();
    }
    setIsVoiceRecognitionRight(0);
    if (similarity >= 0.7) {
      setIsVoiceRecognitionRight(1);
      setNumberOfIncorrectEmojiQuestions(0);

      if (quesNum === numberOfQue - 1) {
        setProblemType(0);
        setQuesNum(0);

        if (curSentence === sentenceLenght - 1) {
          setShowQues(false);
          if (curContent === numberOfContents - 1) {
            setProblemType(3); //end study
            setCurContent(0);
            setQuesNum(0);
            return;
          } else {
            setCurContent((prev) => prev + 1);
          }
        } else {
          setCurSentence((prev) => prev + 1);
        }
      } else {
        setQuesNum((prev) => prev + 1);
      }
    } else {
      setNumberOfIncorrectEmojiQuestions((prev) => prev + 1);
      if (numberOfIncorrectEmojiQuestions == 2) {
        if (quesNum === numberOfQue - 1) {
          setProblemType(0);
          setQuesNum(0);

          if (curSentence === sentenceLenght - 1) {
            setShowQues(false);
            if (curContent === numberOfContents - 1) {
              setProblemType(3); //end study
              setCurContent(0);
              setQuesNum(0);
              return;
            } else {
              setCurContent((prev) => prev + 1);
            }
          } else {
            setCurSentence((prev) => prev + 1);
          }
        } else {
          setQuesNum((prev) => prev + 1);
        }
        setNumberOfIncorrectEmojiQuestions(0);
      }
      setIsVoiceRecognitionRight(-1);
    }
  };

  useEffect(() => {
    const quesTxt = [
      "듣고 따라 읽으세요",
      "이모지를 보며 문장을 말해보세요",
      "섞인 이모지를 보고 문장을 말해보세요.",
    ];
    if (quesNum > 0) {
      axios
        .get("https://uvlqtxcfrh.us14.qoddiapp.com/reading/tts", {
          params: {
            ssml: `<speak>${quesTxt[quesNum]}</speak>`,
          },
        })
        .then((audioRes) => {
          const { audioContent } = audioRes.data;
          const audioBlob = base64ToBlob(audioContent, "audio/mp3");
          const audioUrl = URL.createObjectURL(audioBlob);

          // 오디오 요소 생성 및 바로 실행
          audioElement.current = new Audio(audioUrl);
          audioElement.current.play(); // 바로 오디오 실행
        });
    }
  }, [quesNum]);

  return (
    <div>
      {emojiQues.sentence !== "" ? (
        <div>
          <div className="border border-slate-500 rounded-md">
            {quesNum == 0
              ? "1. 듣고 따라 읽으세요"
              : quesNum == 1
              ? `${quesNum + 1}. 이모지를 보며 문장을 말해보세요`
              : `${quesNum + 1}. 섞인 이모지를 보고 문장을 말해보세요`}
          </div>
          <div className={quesNum < 2 ? "text-base" : "text-2xl"}>
            {quesNum === 0 ? quesTxt : targetQues}
          </div>
          <div className="text-pink-500">
            {isVoiceRecognitionRight === 0
              ? ""
              : isVoiceRecognitionRight === 1
              ? "잘하셨어요!"
              : numberOfIncorrectEmojiQuestions === 2
              ? `힌트! ${sentence}`
              : "틀렸습니다."}
          </div>
          <VoiceRecognitionBar
            listening={listening}
            transcript={transcript}
            toggleListening={toggleListening}
            onSubmitTranscript={
              quesNum === 0 ? onSubmitFirstTranscript : onSubmitTranscript
            }
          />
        </div>
      ) : (
        "🤔이모지 문제 생성중..."
      )}
    </div>
  );
};

export default EmojiQues;
